/**
 * Shared types and presentational helpers used by SidePanel and SidePanelOptimized.
 */
import React, { Fragment } from 'react';
import PanelSection, { type SearchDimension } from './PanelSection';

// Map the paged filter keys to their backend search dimension. Only these
// sections are cursor-paged and virtualised (see usePagedOptions); the rest are
// plain lists from the hierarchy endpoint.
const searchDimensionByKey: Record<string, SearchDimension> = {
  subCustomer: 'subCustomer',
  cityOrigin: 'originCity',
  cityDestination: 'destinationCity',
};

export interface PanelData {
  type: 'radiolist' | 'checklist';
  key: string;
  label: string;
  values: Array<{
    id: string;
    value: string;
    isChecked?: boolean;
  }>;
  selected?: {
    id: string;
    value: string;
  };
}

interface PanelSectionListProps {
  licenseeData: PanelData | null;
  subCompanyData: PanelData | null;
  branchData: PanelData | null;
  subCustomerData: PanelData | null;
  countryOriginData: PanelData | null;
  countryDestinationData: PanelData | null;
  cityOriginData: PanelData | null;
  cityDestinationData: PanelData | null;
  transportModeData: PanelData | null;
  handleSidePanelSelection: (key: string) => void;
  /**
   * When provided (SidePanel), forces the licensee section's render type
   * (legacy behavior hardcoded "checklist"). Otherwise the licensee section
   * uses its own data.type like every other section.
   */
  licenseeType?: 'radiolist' | 'checklist';
  /**
   * When provided (SidePanelOptimized), each section is additionally gated by
   * this predicate. When omitted (SidePanel), every section with data renders.
   */
  shouldShowFilter?: (key: string) => boolean;
}

/**
 * Renders the ordered list of filter PanelSections shared by SidePanel and
 * SidePanelOptimized. Behavior-preserving extraction of the previously
 * duplicated section list in both components.
 */
export const PanelSectionList: React.FC<PanelSectionListProps> = ({
  licenseeData,
  subCompanyData,
  branchData,
  subCustomerData,
  countryOriginData,
  countryDestinationData,
  cityOriginData,
  cityDestinationData,
  transportModeData,
  handleSidePanelSelection,
  licenseeType,
  shouldShowFilter,
}) => {
  const sections: Array<{
    data: PanelData | null;
    filterKey: string;
    type?: 'radiolist' | 'checklist';
  }> = [
    { data: licenseeData, filterKey: 'licensee', type: licenseeType },
    { data: subCompanyData, filterKey: 'subCompany' },
    { data: branchData, filterKey: 'branch' },
    { data: subCustomerData, filterKey: 'subCustomer' },
    { data: countryOriginData, filterKey: 'countryOrigin' },
    { data: countryDestinationData, filterKey: 'countryDestination' },
    { data: cityOriginData, filterKey: 'cityOrigin' },
    { data: cityDestinationData, filterKey: 'cityDestination' },
    { data: transportModeData, filterKey: 'transportMode' },
  ];

  // Filters hidden from the UI for now (still wired; re-enable by removing the key).
  const hiddenFilterKeys = new Set(['subCompany', 'branch']);

  return (
    <Fragment>
      {sections.map(({ data, filterKey, type }) =>
        data && !hiddenFilterKeys.has(filterKey) && (!shouldShowFilter || shouldShowFilter(filterKey)) ? (
          <PanelSection
            key={filterKey}
            panelItem={data}
            type={type ?? data.type}
            handleSidePanelSelection={handleSidePanelSelection}
            searchDimension={searchDimensionByKey[filterKey]}
          />
        ) : null
      )}
    </Fragment>
  );
};
